import { useEffect, useRef, useState } from 'react';
import { useStore } from '@tanstack/react-store';
import { v4 as uuidv4 } from 'uuid';
import type { Connection, SaveConnectionOptions } from '../../../types/electron';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { cn } from '@/lib/utils';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { connectionDefaultValues, type ConnectionFormValues, type DatabaseEngine } from './connectionFormSchema';
import { connectionToFormValues } from './connectionToFormValues';
import { connectionTestFingerprint, useConnectionTestOutcome } from '../hooks/useConnectionTestOutcome';
import { useDraftRevision } from '../hooks/useDraftRevision';
import { useConnectionForm } from '../connection-dialog/state/connectionForm';
import { buildProbeConnection, fingerprintsLiteralPassword } from '../connection-dialog/state/connectionRecord';
import { engineDefaultValues } from '../connection-dialog/state/engineDrafts';
import { useEngineDrafts } from '../connection-dialog/state/useEngineDrafts';
import { useModeReconciliation } from '../connection-dialog/state/reconcileFieldMeta';
import { useAzureProvenance } from '../connection-dialog/state/provenance';
import { initialDialogView } from '../connection-dialog/state/dialogView';
import { invalidatePendingWork, SAVING_REASON, useSaveInFlight, type PendingWork } from '../connection-dialog/state/saveAuthorization';
import { useValidationGate } from '../connection-dialog/hooks/useValidationGate';
import { useConnectionTestRun } from '../connection-dialog/hooks/useConnectionTestRun';
import { useLoadDatabases } from '../connection-dialog/hooks/useLoadDatabases';
import { useConnectionFix } from '../connection-dialog/hooks/useConnectionFix';
import { useConnectionSave } from '../connection-dialog/hooks/useConnectionSave';
import { useStoredPasswordHydration } from '../connection-dialog/hooks/useStoredPasswordHydration';
import { useConnectionDialogFunnel } from '../connection-dialog/hooks/useConnectionDialogFunnel';
import { pendingPrefillEngine, usePasteEntry } from '../connection-dialog/hooks/usePasteEntry';
import { useDialogNavigation } from '../connection-dialog/hooks/useDialogNavigation';
import { useEntryPoints } from '../connection-dialog/hooks/useEntryPoints';
import { useNestedFlows } from '../connection-dialog/hooks/useNestedFlows';
import { useConnectionDelete } from '../connection-dialog/hooks/useConnectionDelete';
import { useDialogLayout } from '../connection-dialog/hooks/useDialogLayout';
import { useFocusRoleMapping } from '../connection-dialog/hooks/useFocusRoleMapping';
import { dialogFocusElement, focusEntryPoint } from '../connection-dialog/hooks/dialogFocus';
import { entryPoint, type EntryPlacement } from '../connection-dialog/start/entryPoints';
import { AzureResourcePicker } from '../azure-picker';
import { openAzureReview } from '../connection-import';
import { looksLikeConnectionString } from '../connection-dialog/paste/pastePreview';
import { saveAnywayRefusal } from '../connection-dialog/pane/resultStripModel';
import { ConnectionDialogHeader } from '../connection-dialog/pane/ConnectionDialogHeader';
import { ConnectionDialogFooter } from '../connection-dialog/pane/ConnectionDialogFooter';
import { ConnectionFormView } from '../connection-dialog/pane/ConnectionFormView';
import { NestedFlowNotice } from '../connection-dialog/pane/NestedFlowNotice';
import { EngineStartView } from '../connection-dialog/start/EngineStartView';

interface NewConnectionPopupProps {
  /**
   * `options` is what this ONE save says about itself — `passwordEdited` when the user retyped the password. Hosts hand
   * it on to `connectionStore.updateConnection` unchanged; it is never a field of `connection`, so a later background
   * re-save of the record cannot carry it.
   */
  onSave: (connection: Connection, options: SaveConnectionOptions) => Promise<boolean>;
  noConnections?: boolean;
  onCancel?: () => void;
  /** If provided, the dialog will be in "edit" mode with pre-filled values */
  editConnection?: Connection;
  /** Handler for deleting the connection (only available in edit mode) */
  onDelete?: (connectionId: string) => Promise<boolean>;
  /** Whether the connection being edited is currently connected */
  isConnected?: boolean;
  prefillHandoffId?: string | null;
  /** Create mode: open straight in this engine's form (with the rail) instead of the start view. */
  initialEngine?: DatabaseEngine;
}

/**
 * The dialog instance belongs to ONE connection (round B R2): editing another connection remounts it, so no result,
 * in-flight test, Load databases or password hydration of the previous connection can reach the next one — every hook's
 * unmount guard discards late completions. Its state and flows live in `connection-dialog/` (Option C, C0 + C1; see
 * `Docs/ConnectionDialog.md`): create mode opens on the start view (or a prefill's form), edit mode in the form alone.
 */
export function NewConnectionPopup(props: NewConnectionPopupProps) {
  return <NewConnectionDialog key={props.editConnection?.id ?? 'new-connection'} {...props} />;
}

function NewConnectionDialog({ onSave, noConnections = false, onCancel, editConnection, onDelete, isConnected = false, prefillHandoffId = null, initialEngine }: NewConnectionPopupProps) {
  const isEditMode = !!editConnection;
  const { track } = useAnalytics();
  const [initialView] = useState(() => initialDialogView({ isEditMode, initialEngine, prefillEngine: pendingPrefillEngine(prefillHandoffId) }));
  const deletion = useConnectionDelete(editConnection, onDelete);
  const [connectionId, setConnectionId] = useState(() => editConnection?.id || uuidv4());
  const submitRef = useRef<(values: ConnectionFormValues) => Promise<void>>(async () => {});
  // Stable across renders: TanStack re-applies CHANGED default values to an untouched form.
  const [defaultValues] = useState(() => (editConnection ? connectionToFormValues(editConnection) : initialEngine ? engineDefaultValues(initialEngine) : connectionDefaultValues));
  const form = useConnectionForm(defaultValues, (values) => submitRef.current(values));
  // Round B: the draft revision a Save / Save anyway is authorized for, captured before its first await.
  const draft = useDraftRevision(form.store, () => form.state.values);
  useEffect(() => () => draft.invalidate(), [draft]);
  useModeReconciliation(form);
  // A picker id whose target is edited stops describing it (C3.1 provenance); a user-typed id is never touched.
  useAzureProvenance(form);

  const engine = useStore(form.store, (state) => state.values.engine);
  const authentication = useStore(form.store, (state) => state.values.authentication);
  // A result belongs to this connection, the draft generation and the values it tested (F17, round B R2).
  const passwordEdited = (dirty: boolean | undefined) => fingerprintsLiteralPassword(isEditMode, dirty);
  const tests = useConnectionTestOutcome({
    owner: connectionId,
    engine,
    renderedFingerprint: useStore(form.store, (state) => connectionTestFingerprint(state.values, { passwordEdited: passwordEdited(state.fieldMeta.password?.isDirty) })),
    fingerprintOf: (values) => connectionTestFingerprint(values ?? form.state.values, { passwordEdited: passwordEdited(form.state.fieldMeta.password?.isDirty) }),
  });

  const gate = useValidationGate({ form, tests, track });
  const probe = (values: ConnectionFormValues) => buildProbeConnection(values, connectionId, editConnection);
  const work = useRef<PendingWork | null>(null);
  const invalidate = (change: { engineChanged: boolean }) => {
    if (work.current) invalidatePendingWork(work.current, change);
  };
  const drafts = useEngineDrafts({ form, onInvalidate: invalidate, programmatic: draft.programmatic, startsDetached: !isEditMode && !initialEngine });
  const testRun = useConnectionTestRun({ form, tests, programmatic: draft.programmatic, runGate: gate.runGate, probe, track });
  const loads = useLoadDatabases({ form, tests, runGate: gate.runGate, probe, adopt: drafts.adoptLoadedDatabases, editConnection, isConnected });
  const fix = useConnectionFix({ form, tests, draft, runTest: () => testRun.run(form.state.values, false, 'test', 'fix'), track });
  const saving = useSaveInFlight();
  // Declared before the funnel: a prefill handoff lands before the funnel takes its "opened" snapshot.
  const paste = usePasteEntry({ drafts, isEditMode, prefillHandoffId, track });
  const funnel = useConnectionDialogFunnel({
    form, isEditMode, noConnections, initialView: initialView.kind, changedSince: drafts.changedSince, track,
    attachedEngine: () => (drafts.isDetached() ? null : form.state.values.engine),
  });
  // State, not a ref: Radix mounts the portal content a render after the dialog (layout and transition measure it).
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const nav = useDialogNavigation({ isEditMode, initialView, form, drafts, paste, funnel, root: content, engine });
  const save = useConnectionSave({
    form, tests, draft, saving, runGate: gate.runGate, runTest: testRun.run, onSave, onCancel, noConnections, editConnection, connectionId, setConnectionId, track,
    saveAnywayRefusal: () => saveAnywayRefusal(tests.visible),
    originOf: drafts.originOf,
    onSaved: () => {
      funnel.markSaved();
      drafts.clearDrafts({ detach: !isEditMode });
      nav.restartAfterSave();
    },
  });
  submitRef.current = save.submit;
  work.current = { abandonFix: fix.abandon, revokeSaves: draft.invalidate, abortTest: testRun.abort, abortLoad: loads.abort, endSaving: saving.clear, newGeneration: tests.bumpGeneration };
  useStoredPasswordHydration(editConnection, drafts.hydratePassword);
  const savingReason = saving.saving ? SAVING_REASON : undefined;

  // Cancel / Esc / X: never blocked by a running test; every pending Test, Load, fix retry and Save is voided — a late
  // success (even one still in validation) saves nothing.
  const dismiss = () => {
    save.markDismissed();
    funnel.trackAbandonment(nav.view.kind);
    invalidate({ engineChanged: false });
    onCancel?.();
  };
  // Browse Azure opens on top (C3.1): the placement it was opened from is the analytics `entry`; null = closed.
  const [azurePickerAt, setAzurePickerAt] = useState<EntryPlacement | null>(null);
  const entries = useEntryPoints({
    track,
    savingReason,
    onPaste: (placement) => (placement === 'start' ? void nav.pasteFromClipboard() : paste.openField()),
    onBrowseAzure: setAzurePickerAt,
  });
  // Pending paste text — the paste field's, or a connection string in the start filter — is input too (C3-04).
  const hasInputNow = () => drafts.hasInputNow() || paste.hasPendingText() || looksLikeConnectionString(nav.filter);
  // Import / Detect open on top (C3): a completion with no draft input closes the dialog — no abandonment.
  const nested = useNestedFlows({
    enabled: !isEditMode,
    hasInputNow,
    onKept: funnel.markImported,
    restoreFocus: () => focusEntryPoint(entryPoint(entries.lastEntry()).testId),
    onCompletedWithoutInput: (via) => {
      save.markDismissed();
      funnel.completedByImport(via);
      invalidate({ engineChanged: false });
      onCancel?.();
    },
  });
  const notice = <NestedFlowNotice notice={nested.notice} />;

  const beforeViewportChange = useRef<() => void>(() => {});
  const layout = useDialogLayout({ mode: isEditMode ? 'edit' : 'create', root: content, beforeViewportChange, measureKey: `${nav.view.kind}:${tests.shown || testRun.testRun ? 1 : 0}` });
  beforeViewportChange.current = useFocusRoleMapping(`${nav.view.kind}:${layout.columns}`);

  const filterRef = useRef<HTMLInputElement>(null);
  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && dismiss()}>
      <DialogContent
        ref={setContent}
        className={cn(
          'flex max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none',
          isEditMode ? 'w-[min(720px,calc(100vw-32px))]' : layout.columns === 'two-pane' ? 'w-[min(900px,calc(100vw-10rem))]' : 'w-[calc(100vw-32px)]',
          // The Test results region's cap (`RESULTS_REGION_CLASS`) is measured against this height: change both together.
          layout.short ? 'h-[calc(100dvh-16px)]' : 'h-[min(680px,90vh)]',
        )}
        data-testid={isEditMode ? 'edit-connection-dialog' : 'new-connection-dialog'}
        data-view={nav.view.kind}
        data-layout={layout.columns}
        data-height-tier={layout.veryShort ? 'very-short' : layout.short ? 'short' : 'normal'}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          dialogFocusElement(nav.view.kind === 'start' ? 'filter' : isEditMode || initialEngine ? 'name' : 'prefill', form.state.values)?.focus();
        }}
        // One Radix layer stack (package.json pins `@radix-ui/react-dismissable-layer`): a menu or dialog opened over this
        // one takes its own Escape, so this runs only when the dialog is the top layer.
        onEscapeKeyDown={nav.onEscape}
        // An overlay click never throws away input (D9): only an untouched dialog closes on it.
        onInteractOutside={(event) => hasInputNow() && event.preventDefault()}
      >
        <ConnectionDialogHeader isEditMode={isEditMode} short={layout.short} onClose={dismiss} />
        <p role="status" aria-live="polite" className="sr-only" data-testid="connection-dialog-announcer">
          {nav.announcement}
        </p>
        {nav.view.kind === 'start' ? (
          <>
            <EngineStartView
              filter={nav.filter}
              filterVia={nav.filterVia}
              onFilterChange={nav.setFilter}
              initialActive={nav.returnActive}
              onPickEngine={(next, via) => nav.pick(next, via)}
              entries={entries}
              paste={{ previewFor: paste.previewFor, onContinue: nav.continuePaste, hint: nav.pasteHint }}
              notice={notice}
              announce={nav.announce}
              filterRef={filterRef}
              short={layout.short}
              track={track}
            />
            <ConnectionDialogFooter startView isEditMode={false} short={layout.short} testing={null} deleting={false} onTest={() => {}} onCancel={dismiss} />
          </>
        ) : (
          <ConnectionFormView
            form={form}
            engine={engine}
            authentication={authentication}
            isEditMode={isEditMode}
            layout={layout}
            drafts={drafts}
            nav={nav}
            entries={entries}
            paste={paste}
            notice={notice}
            tests={tests}
            testRun={testRun}
            loads={loads}
            gate={gate}
            onSubmitForm={() => void save.saveForm()}
            onApplyFix={(chosen) => void fix.apply(chosen)}
            onSaveAnyway={() => void save.saveAnyway()}
            onAzurePick={nav.applyAzurePick}
            savingReason={savingReason}
            deleting={deletion.deleting}
            deleteError={deletion.deleteError}
            onDelete={deletion.onDelete}
            onCancel={dismiss}
            track={track}
          />
        )}
        {azurePickerAt ? (
          <AzureResourcePicker
            open
            entryMode
            entry={azurePickerAt}
            onOpenChange={(isOpen) => !isOpen && setAzurePickerAt(null)}
            onPick={nav.applyAzurePick}
            // Several picks: today's review opens over this dialog and completes through the host (`via: 'azure_review'`).
            onPickMany={(picked) => openAzureReview(picked, 'connection_dialog')}
            onCloseAutoFocus={(event, outcome) => {
              // A pick placed focus on the form's first required empty field; Cancel returns it to the entry (plan §3.6).
              if (outcome === 'review') return;
              event.preventDefault();
              if (outcome === 'cancelled') focusEntryPoint(entryPoint('browse_azure').testId);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
